import api from './api';

export interface ReportOptions {
  status: string;
  gid: number;
  plot_details: {
    area_sqm: number;
    area_sqft?: number; // Optional: may not be returned by API
    zone: string;
    tall_building_eligible: boolean;
    nearby_wide_roads?: any[];
  };
  fsi_options: FSIOption[];
  zone_fsi_config: {
    base_fsi: number;
    max_paid_fsi: number;
    total_max_fsi: number;
    tdr_allowed: boolean;
    tdr_max_additional: number;
  };
  development_scenarios: DevelopmentScenario[];
}

export interface FSIOption {
  id: string;
  name: string;
  description: string;
  cost_multiplier: number;
  requires_approval: boolean;
}

export interface DevelopmentScenario {
  id: string;
  category: 'residential' | 'commercial';
  name: string;
  description: string;
  cost_per_sqft: number;
  min_area_sqft: number;
  max_floors?: number;
  suitable_for: string[];
}

export interface CustomReportRequest {
  fsi_utilization: string;
  development_scenario: string;
  custom_options?: {
    floor_count?: number;
    basement_count?: number;
    has_tdr?: boolean;
    tdr_area_sqm?: number;
  };
}

export interface CustomReportResponse {
  status: string;
  gid: number;
  plot_details: {
    area_sqm: number;
    area_sqft: number;
    zone: string;
    tall_building_eligible: boolean;
    nearby_wide_roads: any[];
  };
  selected_options: {
    fsi_utilization: {
      type: string;
      name: string;
      description: string;
      cost_multiplier: number;
    };
    development_scenario: {
      id: string;
      name: string;
      cost_per_sqft: number;
      category: string;
    };
    floor_count: number;
    basement_count: number;
    has_tdr: boolean;
    tdr_area_sqm: number;
  };
  fsi_breakdown: {
    zone_base_fsi: number;
    max_paid_fsi: number;
    total_max_fsi: number;
    tdr_max_additional: number;
    selected_fsi: number;
    fsi_components: {
      base: number;
      premium: number;
      paid: number;
      tdr: number;
    };
  };
  area_calculations: {
    plot_area_sqm: number;
    plot_area_sqft: number;
    total_fsi: number;
    base_buildable_area_sqm: number;
    tdr_additional_area_sqm: number;
    total_built_up_area_sqm: number;
    total_built_up_area_sqft: number;
    floor_details: {
      total_floors: number;
      floor_plate_area_sqm: number;
      floor_plate_area_sqft: number;
      basement_floors: number;
      above_ground_floors: number;
      tall_building_status: string;
      reduction_percentage: number;
    };
    effective_buildable_area_sqm: number;
    effective_buildable_area_sqft: number;
  };
  cost_analysis: {
    scenario_name: string;
    base_cost_per_sqft: number;
    fsi_utilization_multiplier: number;
    effective_cost_per_sqft: number;
    effective_cost_per_sqm: number;
    buildable_area_sqm: number;
    buildable_area_sqft: number;
    total_construction_cost_inr: number;
    total_construction_cost_crores: number;
    requires_approval: boolean;
  };
  summary: {
    development_type: string;
    zone: string;
    plot_area_sqm: number;
    total_fsi_utilized: number;
    total_buildable_area_sqm: number;
    number_of_floors: number;
    floor_plate_area_sqm: number;
    total_construction_cost_inr: number;
    cost_per_sqm: number;
    tall_building: boolean;
    tdr_utilized: boolean;
    basement_floors: number;
    estimated_sale_value_inr: number;
    gross_profit_inr: number;
    profit_margin_percentage: number;
  };
  specialized_analysis?: {
    tall_building_requirements?: any;
    basement_development?: any;
    tdr_utilization?: any;
    paid_fsi_details?: any;
    commercial_development?: any;
    residential_development?: any;
  };
  important_notes?: Array<{
    type: string;
    icon: string;
    message: string;
  }>;
}

export interface TallBuildingError {
  status: string;
  error_type: string;
  message: string;
  requested_floors: number;
  tall_building_eligible: boolean;
  restrictions: {
    primary_road_width_m: number;
    max_building_height_m: number;
    max_floors_allowed: number;
    zone_restriction: number;
    calculation_basis: string;
  };
  tall_building_requirements: {
    minimum_road_width_required: string;
    search_radius: string;
    nearby_roads_checked: number;
    widest_road_found_m: number;
    gap_to_requirement_m: number;
  };
  alternatives: {
    option_1: any;
    option_2: any;
    option_3: any;
  };
  recommendation: string;
  nearest_wide_road_info: {
    widest_found: string;
    required: string;
    shortage: string;
  };
}

export const fsiReportApi = {
  // Get report options for a plot
  getReportOptions: async (gid: number): Promise<ReportOptions> => {
    const response = await api.get(`/fsi/report-options/${gid}`);
    return response.data;
  },

  // Generate custom FSI report
  generateCustomReport: async (
    gid: number,
    payload: CustomReportRequest
  ): Promise<CustomReportResponse> => {
    const response = await api.post(`/fsi/generate-custom-report/${gid}`, payload);
    return response.data;
  },
};
